"use client";

import Link from "next/link";
import { useState } from "react";
import type { PhoneCodeLoginAccent } from "@/components/auth/PhoneCodeLoginForm";
import { normalizeChinaMobile } from "@/lib/chinaMobile";
import type { AuthPortal } from "@/lib/portalTokens";

const accentStyles: Record<
  PhoneCodeLoginAccent,
  { ring: string; btn: string; link: string }
> = {
  indigo: {
    ring: "focus:ring-indigo-500",
    btn: "bg-indigo-600 hover:bg-indigo-700",
    link: "text-indigo-600",
  },
  green: {
    ring: "focus:ring-green-500",
    btn: "bg-green-600 hover:bg-green-700",
    link: "text-green-600",
  },
  blue: {
    ring: "focus:ring-blue-500",
    btn: "bg-blue-600 hover:bg-blue-700",
    link: "text-blue-600",
  },
};

export type PhonePasswordLoginFormProps = {
  accent: PhoneCodeLoginAccent;
  portal: AuthPortal;
  forgotPasswordHref: string;
  onLogin: (mobile: string, password: string) => Promise<void>;
};

export default function PhonePasswordLoginForm({
  accent,
  portal: _portal,
  forgotPasswordHref,
  onLogin,
}: PhonePasswordLoginFormProps) {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const styles = accentStyles[accent];
  const inputClass = `w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${styles.ring}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedMobile = normalizeChinaMobile(mobile);
    if (!mobile.trim()) {
      setError("请输入手机号");
      return;
    }
    if (!normalizedMobile) {
      setError("请输入正确的中国大陆手机号");
      return;
    }
    if (!password) {
      setError("请输入密码");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onLogin(normalizedMobile, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={(e) => void handleSubmit(e)} className="w-full">
        <div className="flex flex-col gap-4">
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            autoFocus
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="输入手机号"
            className={inputClass}
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="输入密码"
              className={`${inputClass} pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {showPassword ? "隐藏" : "显示"}
            </button>
          </div>
          <button
            type="submit"
            disabled={!mobile.trim() || !password || loading}
            className={`w-full px-6 py-3 text-white rounded-lg disabled:opacity-50 transition-colors ${styles.btn}`}
          >
            {loading ? "正在登录" : "登录"}
          </button>
          <div className="text-center">
            <Link href={forgotPasswordHref} className={`text-sm hover:underline ${styles.link}`}>
              忘记密码？
            </Link>
          </div>
        </div>
      </form>
      {error && <p className="text-red-500 mt-4 text-center">{error}</p>}
    </>
  );
}
